import CardTitleWithIcon from "@/components/company/card-title-with-icon";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "@/hooks/useTranslation";
import {
  employeeReviewFigures,
  type EmployeeReviewFigures,
} from "@/lib/company-employee-reviews";
import type { CompanyDTO } from "@shaarp/common";
import { SmileIcon } from "lucide-react";
import { useMemo } from "react";

type ScoreFormatProps = {
  readonly scoreFormat: Intl.NumberFormat;
};

function ScoreHeadline({
  score,
  count,
  scoreFormat,
}: ScoreFormatProps & { readonly score: number; readonly count: number }) {
  const { t } = useTranslation();

  return (
    <div className="flex items-baseline gap-2">
      <span className="text-3xl font-semibold tabular-nums">
        {scoreFormat.format(score)}
      </span>
      <span className="text-sm text-muted-foreground">
        {count > 0
          ? t("company.employeeReviews.basedOn", { count })
          : t("company.employeeReviews.score")}
      </span>
    </div>
  );
}

function SubScores({
  scores,
  scoreFormat,
}: ScoreFormatProps & { readonly scores: EmployeeReviewFigures["scores"] }) {
  const { t } = useTranslation();

  return (
    <dl className="flex flex-col gap-2">
      {scores.map(({ key, value }) => (
        <div key={key} className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">
              {t(`company.employeeReviews.scores.${key}`)}
            </dt>
            <dd className="font-medium tabular-nums">
              {scoreFormat.format(value)}
            </dd>
          </div>
          <Progress value={(value / 5) * 100} className="h-1.5" />
        </div>
      ))}
    </dl>
  );
}

function ReviewerShares({
  shares,
}: {
  readonly shares: EmployeeReviewFigures["shares"];
}) {
  const { t, currentLanguage } = useTranslation();

  return (
    <dl className="grid grid-cols-1 gap-2 @xs:grid-cols-3">
      {shares.map(({ key, percent }) => (
        <div
          key={key}
          className="flex flex-col gap-0.5 rounded-lg border border-border p-2"
        >
          <dt className="order-2 text-xs text-muted-foreground">
            {t(`company.employeeReviews.shares.${key}`)}
          </dt>
          <dd className="order-1 text-lg font-semibold tabular-nums">
            {new Intl.NumberFormat(currentLanguage, {
              style: "percent",
            }).format(percent / 100)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function StarDistribution({
  distribution,
  totalStars,
}: {
  readonly distribution: EmployeeReviewFigures["distribution"];
  readonly totalStars: number;
}) {
  const { t } = useTranslation();

  return (
    <dl className="flex flex-col gap-1">
      {distribution.map(({ stars, count }) => (
        <div key={stars} className="flex items-center gap-2 text-xs">
          <dt className="w-16 shrink-0 text-muted-foreground">
            {t("company.employeeReviews.starsLabel", { count: stars })}
          </dt>
          <Progress
            value={(count / totalStars) * 100}
            className="h-1.5 flex-1"
          />
          <dd className="w-8 shrink-0 text-right tabular-nums">{count}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * What employees say of the company on Glassdoor, Indeed and the other
 * platforms Coresignal aggregates: the overall score, the sub-scores, the
 * shares of reviewers, and how many gave each star. `withScore={false}`
 * beside a card that already shows the score: the breakdown alone, or nothing.
 */
export default function CardEmployeeReviews({
  company,
  withScore = true,
}: {
  readonly company: CompanyDTO;
  readonly withScore?: boolean;
}) {
  const { t, currentLanguage } = useTranslation();
  const reviews = company.employeeReviews;
  const figures = useMemo(() => employeeReviewFigures(reviews), [reviews]);
  const scoreFormat = useMemo(
    () =>
      new Intl.NumberFormat(currentLanguage, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
    [currentLanguage],
  );

  if (!reviews) return null;
  const count = reviews.count ?? 0;
  const score = withScore ? reviews.aggregateScore : null;
  const totalStars = figures.distribution.reduce(
    (sum, { count: starCount }) => sum + starCount,
    0,
  );
  // The headline is the score: a count alone has nothing to head.
  const hasHeadline = score != null;
  if (
    !hasHeadline &&
    figures.scores.length === 0 &&
    figures.shares.length === 0 &&
    totalStars === 0
  ) {
    return null;
  }

  return (
    <Card className="flex flex-col min-w-0 overflow-hidden">
      <CardHeader>
        <CardTitleWithIcon icon={SmileIcon} title={t("company.employeeReviews.title")} />
        <CardDescription>{t("company.employeeReviews.source")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {score != null && (
          <ScoreHeadline score={score} count={count} scoreFormat={scoreFormat} />
        )}
        {figures.scores.length > 0 && (
          <SubScores scores={figures.scores} scoreFormat={scoreFormat} />
        )}
        {figures.shares.length > 0 && (
          <ReviewerShares shares={figures.shares} />
        )}
        {totalStars > 0 && (
          <StarDistribution
            distribution={figures.distribution}
            totalStars={totalStars}
          />
        )}
      </CardContent>
    </Card>
  );
}
